import React, { useState } from "react";

import { Col, Row } from "react-bootstrap";

import { ParticipantTypes, RelatedUsersFragment, UserStatus } from "@api/graphql";
import { PenWriteEditIcon } from "@assets/icons";
import { IconLink } from "@components/IconLink";
import { CustomAccordion } from "@components/accordion/CustomAccordion";
import DetailsRow from "@components/account_profile/DetailsRow";
import { ParticipantStatusBadge } from "@components/account_profile/ParticipantStatusBadge";
import { useIsAuthUserAllowed } from "@components/permission_control/ProtectedComponent";
import { Role } from "@roles";

import { RelatedUsersModal } from "./RelatedUsersModal";
import { ResetPasswordButton } from "./ResetPasswordButton";

interface RelatedUsersProps {
  relatedUsers: (RelatedUsersFragment | undefined)[];
  isInternalUser: boolean;
  participantTypes: (ParticipantTypes | undefined)[];
  refetchAccountDetails: () => void;
}

const RelatedUsersAccordion: React.FC<RelatedUsersProps> = ({
  relatedUsers,
  isInternalUser,
  participantTypes,
  refetchAccountDetails,
}) => {
  const isVAOrMs = useIsAuthUserAllowed([Role.VisualArtistAdmin, Role.MemberServices]);
  const isMs = useIsAuthUserAllowed([Role.MemberServices]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<RelatedUsersFragment | null>(null);

  const shouldShow =
    isInternalUser ||
    participantTypes.includes(ParticipantTypes.CMO) ||
    participantTypes.includes(ParticipantTypes.ART_CENTRE) ||
    participantTypes.includes(ParticipantTypes.AMP);
  const handleEditClick = (user: RelatedUsersFragment) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };
  if (!shouldShow) {
    return null;
  }

  const isUserPendingOrActive = (relatedUser: RelatedUsersFragment): boolean => {
    return relatedUser.user_status === UserStatus.ACTIVE || relatedUser.user_status === UserStatus.PENDING_APPROVAL;
  };

  return (
    <CustomAccordion
      accordionKey={"relatedUsers"}
      contrastOpenStyle={false}
      header={<h5 className="mb-0">Other contacts</h5>}
      defaultActiveKey={"relatedUsers"}
      customHeaderStyle="py-4"
      preventCloseOnContentClick
      showBottomLine
    >
      <Row>
        {relatedUsers
          .filter((user): user is RelatedUsersFragment => user !== undefined)
          .map((user) => (
            <Col md={12} key={user.email} className="d-flex flex-column mb-4">
              <div className="ms-auto">
                {isVAOrMs && <IconLink src={PenWriteEditIcon} text="Edit" onClick={() => handleEditClick(user)} />}
              </div>
              <DetailsRow
                details={[
                  {
                    title: "Full name",
                    value: user.full_name || "-",
                  },
                  ...(user.user_status
                    ? [
                        {
                          title: "User status",
                          value: <ParticipantStatusBadge status={user.user_status} />,
                          isBadge: true,
                        },
                      ]
                    : []),
                  {
                    title: "Email",
                    value: user.email || "-",
                  },
                  {
                    title: "Mobile number",
                    value: user.phone_number || "-",
                  },
                  ...(isMs && isUserPendingOrActive(user) // only ms team can reset users' password,
                    ? [
                        {
                          title: "",
                          value: user.user_id ? <ResetPasswordButton userId={user.uuid!} /> : <>User ID Missing</>,
                          isBadge: true,
                        },
                      ]
                    : []),
                ]}
                className="mt-4"
              />
            </Col>
          ))}

        <RelatedUsersModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
          }}
          selectedUser={selectedUser ?? {}}
          refetchAccountDetails={refetchAccountDetails}
        />
      </Row>
    </CustomAccordion>
  );
};
export default RelatedUsersAccordion;
